import type { TimeInput, ZoneAbbrevStyle } from './types';
import { toDate } from './isomorphic/internal/coerce';
import { safeFormat } from './isomorphic/internal/safe-format';
import { deriveZoneAbbrev } from './derive-zone-abbrev';

export interface FormatEventTimeOptions {
    /** Drop the year ("Mar 15, 2:30 PM CDT"). */
    omitYear?: boolean;
    /** Render time only ("2:30 PM CDT"). */
    timeOnly?: boolean;
    /** Render date only, with no zone label ("Mar 15, 2026"). */
    dateOnly?: boolean;
    /**
     * Add a seconds component ("Mar 15, 2:30:30 PM CDT"). Default false.
     *
     * Opt-in because this formatter is the single display path for every date
     * in both apps — the admin Lot Order tab needs second-level precision to
     * make a sub-minute close stagger readable (BL-1557), and nothing else
     * does. Ignored for `dateOnly`, which carries no time of day.
     */
    showSeconds?: boolean;
    /** Append the machine-derived zone abbreviation. Default true (ignored for `dateOnly`). */
    showZone?: boolean;
    /** Abbreviation style when `showZone` — `specific` (CDT) or `generic` (CT). */
    abbrevStyle?: ZoneAbbrevStyle;
    /** Fallback string for invalid/empty input. Default "—". */
    placeholder?: string;
}

/**
 * Format a fixed instant in a given IANA zone, with a machine-derived zone label.
 *
 * Date/time parts are formatted in-zone via `Intl`, then the zone abbreviation
 * is appended from {@link deriveZoneAbbrev} — NOT from Intl's own
 * `timeZoneName`, which returns useless `GMT±N` for non-US zones. This is the
 * single display formatter that subsumes the old admin `formatDateTime` /
 * `formatShortDateTime` / `formatTime` / `formatTimeShort` and the bidder
 * close-time/opening-line formatters.
 *
 * For values stored in a **`date` column** use {@link formatLogicalDate} instead —
 * those must stay UTC-anchored and must never carry a zone. The test is the
 * stored column type, not whether the surface shows a time (BUG-1591): a
 * `timestamptz` rendered as a bare day still belongs here, because which day it
 * falls on is zone-dependent. See ADR 024 — Time Model § 3b.
 *
 * @sideEffect none — pure.
 */
export function formatEventTime(
    instant: TimeInput,
    zone: string | undefined,
    opts: FormatEventTimeOptions = {},
): string {
    const placeholder = opts.placeholder ?? '—';
    // `timeOnly` + `dateOnly` together would leave no date/time components and
    // defer to an implementation-defined Intl default — a nonsensical combo.
    if (opts.timeOnly && opts.dateOnly) return placeholder;
    const d = toDate(instant);
    if (!d) return placeholder;

    const fmt: Intl.DateTimeFormatOptions = {};
    if (!opts.timeOnly) {
        fmt.month = 'short';
        fmt.day = 'numeric';
        if (!opts.omitYear) fmt.year = 'numeric';
    }
    if (!opts.dateOnly) {
        fmt.hour = 'numeric';
        fmt.minute = '2-digit';
        if (opts.showSeconds) fmt.second = '2-digit';
    }
    if (zone) fmt.timeZone = zone;

    let out = safeFormat(d, fmt);

    const showZone = (opts.showZone ?? true) && !opts.dateOnly;
    if (showZone) {
        const abbrev = deriveZoneAbbrev(d, zone, { style: opts.abbrevStyle });
        if (abbrev) out = `${out} ${abbrev}`;
    }
    return out;
}
